/**
 * The unified map's view state as a URL query string.
 *
 * The map's URL is the single source of truth for what the user is looking at:
 * which group, which status/category/load-type filters, the search text, the
 * list ordering and the viewport.
 * That makes a map link shareable and makes reload plus browser back/forward
 * work without any stored session state. This module owns the codec both ways.
 *
 * Unset values are omitted from the string, so a pristine map carries a bare
 * URL and every parameter that is present was chosen by the user.
 *
 * Contains:
 * - DEFAULT_MAP_STATE / BBOX_PRECISION — vocab + defaults
 * - parseMapSearch     — query string -> state
 * - serializeMapSearch — state -> query string (no leading `?`)
 *
 * Returns plain JS — no JSX, no redux.
 */
import { SITE_STATUS, SORT_DIRECTION, SORT_FIELD } from '@sophocles/features/map/mapState';

// Five decimal places is roughly a metre of latitude — finer than any map
// viewport distinction, and short enough to keep a shared URL readable.
export const BBOX_PRECISION = 5;

const BBOX_EDGES = Object.freeze(['south', 'west', 'north', 'east']);

export const DEFAULT_MAP_STATE = Object.freeze({
  group: null,
  status: Object.freeze([]),
  cat: Object.freeze([]),
  lt: Object.freeze([]),
  q: '',
  sort: Object.freeze({ field: SORT_FIELD.NAME, direction: SORT_DIRECTION.ASC }),
  bbox: null,
});

const STATUS_VALUES = new Set(Object.values(SITE_STATUS));
const SORT_FIELD_VALUES = new Set(Object.values(SORT_FIELD));
const SORT_DIRECTION_VALUES = new Set(Object.values(SORT_DIRECTION));

function parseList(raw, allowed) {
  if (!raw) return [];
  const seen = new Set();
  raw.split(',').forEach(part => {
    const value = part.trim();
    if (!value) return;
    if (allowed && !allowed.has(value)) return;
    seen.add(value);
  });
  return [...seen];
}

function parseOneOf(raw, allowed, fallback) {
  return raw && allowed.has(raw) ? raw : fallback;
}

function parseSort(raw) {
  const [field, direction] = (raw ?? '').split(':');
  if (!SORT_FIELD_VALUES.has(field) || !SORT_DIRECTION_VALUES.has(direction)) return DEFAULT_MAP_STATE.sort;
  return { field, direction };
}

function parseBbox(raw) {
  if (!raw) return null;
  const parts = raw.split(',').map(Number);
  if (parts.length !== BBOX_EDGES.length || parts.some(value => !Number.isFinite(value))) return null;
  return BBOX_EDGES.reduce((bbox, edge, index) => ({ ...bbox, [edge]: parts[index] }), {});
}

/**
 * Read the map's view state out of a location search string. Anything missing,
 * malformed or outside the known vocabulary falls back to its default, so a
 * hand-edited or stale URL renders a working map rather than an error.
 *
 * @param {string} searchString - a location search, with or without the leading `?`.
 * @returns {{ group: ?string, status: string[], cat: string[], lt: string[],
 *   q: string,
 *   sort: { field: string, direction: string },
 *   bbox: ?{ south: number, west: number, north: number, east: number } }}
 */
export function parseMapSearch(searchString) {
  const params = new URLSearchParams(searchString ?? '');
  return {
    group: params.get('group') || null,
    status: parseList(params.get('status'), STATUS_VALUES),
    cat: parseList(params.get('cat'), null),
    lt: parseList(params.get('lt'), null),
    q: params.get('q') ?? DEFAULT_MAP_STATE.q,
    sort: parseSort(params.get('sort')),
    bbox: parseBbox(params.get('bbox')),
  };
}

function roundEdge(value) {
  return Number(value.toFixed(BBOX_PRECISION));
}

function serializeBbox(bbox) {
  const edges = BBOX_EDGES.map(edge => Number(bbox[edge]));
  if (edges.some(value => !Number.isFinite(value))) return null;
  return edges.map(roundEdge).join(',');
}

/**
 * Render map view state back into a query string, omitting every value that is
 * still at its default. Round-trips through `parseMapSearch`; bbox edges are
 * rounded to `BBOX_PRECISION` so a pan writes a stable, short URL.
 *
 * @param {object} [state] - a partial map state; missing keys take their default.
 * @returns {string} a query string with no leading `?` (empty when nothing is set).
 */
export function serializeMapSearch(state = {}) {
  const merged = { ...DEFAULT_MAP_STATE, ...state };
  const params = new URLSearchParams();

  if (merged.group) params.set('group', String(merged.group));
  if (merged.status?.length) params.set('status', merged.status.join(','));
  if (merged.cat?.length) params.set('cat', merged.cat.join(','));
  if (merged.lt?.length) params.set('lt', merged.lt.join(','));
  if (merged.q) params.set('q', merged.q);

  const sort = merged.sort ?? DEFAULT_MAP_STATE.sort;
  if (sort.field !== DEFAULT_MAP_STATE.sort.field || sort.direction !== DEFAULT_MAP_STATE.sort.direction) {
    params.set('sort', `${sort.field}:${sort.direction}`);
  }

  const bbox = merged.bbox ? serializeBbox(merged.bbox) : null;
  if (bbox) params.set('bbox', bbox);

  return params.toString();
}
